import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ThemeContextValue,
  ThemeMode,
  ThemeProviderProps,
} from './theme-context-types'
import { ThemeContext } from './use-theme'

const STORAGE_KEY = 'quickpass:theme'
const ROOT_DATA_ATTRIBUTE = 'data-theme'

const isThemeMode = (value: string | null): value is ThemeMode => {
  return value === 'light' || value === 'cozy' || value === 'dark'
}

/**
 * Picks the next theme in the light → cozy → dark → light cycle.
 *
 * @param {ThemeMode} current - the current theme
 * @returns {ThemeMode} the next theme in the cycle
 */
const nextThemeInCycle = (current: ThemeMode): ThemeMode => {
  if (current === 'light') {
    return 'cozy'
  }

  if (current === 'cozy') {
    return 'dark'
  }

  return 'light'
}

/**
 * Wraps the app and exposes a theme value (`light` | `cozy` | `dark`).
 * Applies the current theme to `<html data-theme="...">` so CSS can
 * react to it via the [data-theme="..."] selector. Persists to
 * localStorage so the choice survives reloads.
 *
 * @param {ThemeProviderProps} props
 * @returns {JSX.Element}
 */
const ThemeProvider = ({ children }: ThemeProviderProps): JSX.Element => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') {
      return 'light'
    }

    const stored = window.localStorage.getItem(STORAGE_KEY)
    return isThemeMode(stored) === true ? (stored as ThemeMode) : 'light'
  })

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    document.documentElement.setAttribute(ROOT_DATA_ATTRIBUTE, theme)
  }, [theme])

  const setTheme = useCallback((next: ThemeMode): void => {
    setThemeState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch (error) {
      console.warn('[theme] failed to persist', error)
    }
  }, [])

  const cycleTheme = useCallback((): void => {
    setThemeState(previous => {
      const next = nextThemeInCycle(previous)

      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch (error) {
        console.warn('[theme] failed to persist', error)
      }

      return next
    })
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, cycleTheme }),
    [theme, setTheme, cycleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export { ThemeProvider }
